import { trackAnalyticsEvent } from '@autone/analytics';
import { useAutoneTranslation } from '@autone/translations';
import { createJobPollingHook } from '@autone/utils';
import { enqueueSnackbar } from 'notistack';
import { useCallback } from 'react';

import { type APP_NAME } from '../consts';
import {
  POLLING_INTERVAL_SECONDS,
  POLLING_TIMEOUT_SECONDS,
} from '../consts/polling-interval';
import {
  type AssortmentTemplateImportErrorCode,
  useCreateAssortmentTemplateImportJobMutation,
  usePollAssortmentTemplateImportJobQuery,
} from '../redux/assortment';

const useCreateAssortmentTemplateImportJob = createJobPollingHook({
  useCreateJobMutation: useCreateAssortmentTemplateImportJobMutation,
  usePollJobQuery: usePollAssortmentTemplateImportJobQuery,
  getPollJobIsSuccess: (pollJobResult) => pollJobResult.status === 'completed',
  getPollJobIsError: (pollJobResult) => pollJobResult.status === 'failed',
  getPollJobQueryFromCreateJobResult: (createJobResult) => ({
    path: {
      job_id: createJobResult.id,
    },
  }),
  getAreSameJobs: (createJobResult, pollJobResult) =>
    createJobResult.id === pollJobResult.id,
  pollingIntervalSeconds: POLLING_INTERVAL_SECONDS,
  pollingTimeoutSeconds: POLLING_TIMEOUT_SECONDS,
});

interface UseAssortmentTemplateImportProps {
  onImportUrlReady: (url: string) => Promise<void> | void;
  onImportFailed: (errorCode: AssortmentTemplateImportErrorCode) => void;
}

export const useAssortmentTemplateImport = ({
  onImportUrlReady,
  onImportFailed,
}: UseAssortmentTemplateImportProps) => {
  const { common } = useAutoneTranslation<typeof APP_NAME>();

  const { createJob, jobIsRunning, jobIsSuccess, jobIsError, pollJobResult } =
    useCreateAssortmentTemplateImportJob({
      onJobStarted: ({ createJobResult }) => {
        const { s3_url } = createJobResult;
        if (s3_url) {
          onImportUrlReady(s3_url);
        }
      },
      onJobSuccess: ({ createJobOriginalArgs, durationSeconds }) => {
        const { aggregation_type } = createJobOriginalArgs.body;
        trackAnalyticsEvent({
          category: 'assortment_template_import_export',
          action: 'upload_template_modal:upload_succeeded',
          properties: {
            aggregation_type,
            duration_seconds: durationSeconds,
          },
        });
        enqueueSnackbar({
          message: common('import-flow.upload-processing-successful'),
          variant: 'success',
        });
      },
      onJobError: ({ pollJobResult }) => {
        enqueueSnackbar({ message: common('import-failed'), variant: 'error' });
        const errorCode = pollJobResult?.error_code;
        if (errorCode) {
          onImportFailed(errorCode);
        } else {
          onImportFailed('UNKNOWN_ERROR');
        }
      },
    });

  const startTemplateImport = useCallback(
    async (...args: Parameters<typeof createJob>) => {
      await createJob(...args);
    },
    [createJob],
  );

  const importIsLoading = jobIsRunning;
  const importIsError = jobIsError;
  const importIsSuccess = jobIsSuccess;

  return {
    startTemplateImport,
    importIsLoading,
    importIsError,
    importIsSuccess,
    importJobData: pollJobResult,
  };
};
